import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";
import { Mail, MessageSquare, Clock, ChevronLeft, Send, CheckCircle2, Loader2 } from "lucide-react";

export default function ContactPage() {
  const { name: platformName, logo_base64 } = usePlatform();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  useEffect(() => {
    document.title = `تواصل معنا — ${platformName || "جسر"}`;
    window.scrollTo(0, 0);
  }, [platformName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    await new Promise(r => setTimeout(r, 1400));
    setStatus("sent");
  };

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const inputCls = "w-full bg-white border border-slate-200 px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 transition-all text-slate-800 placeholder:text-slate-400";

  return (
    <div dir="rtl" className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Tajawal', sans-serif" }}>

      {/* Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logo_base64
              ? <img src={logo_base64} alt={platformName} className="w-7 h-7 rounded-lg object-contain" />
              : <JasrMark className="w-7 h-7" />}
            <span className="font-bold text-slate-900">{platformName || "جسر"}</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004EEB] transition-colors">
            <ChevronLeft className="w-4 h-4" />
            العودة للرئيسية
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-[#030B1A] py-14 sm:py-20 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(0,78,235,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,78,235,0.04) 1px, transparent 1px)`,
          backgroundSize: "40px 40px"
        }} />
        <div className="absolute top-0 right-1/3 w-96 h-96 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,78,235,0.12) 0%, transparent 65%)" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-bold"
            style={{ background: "rgba(0,78,235,0.15)", border: "1px solid rgba(0,78,235,0.3)", color: "#93C5FD" }}>
            <MessageSquare className="w-3.5 h-3.5" /> الدعم
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4">تواصل معنا</h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
            فريقنا جاهز للمساعدة. أرسل لنا رسالتك وسنرد خلال 24 ساعة.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Contact info cards */}
          <div className="lg:col-span-1 flex flex-col gap-5">
            {[
              {
                icon: Mail,
                color: "#3B82F6",
                title: "البريد الإلكتروني",
                desc: "للاستفسارات العامة والدعم التقني",
                value: "support@getjisr.com",
                href: "mailto:support@getjisr.com",
              },
              {
                icon: MessageSquare,
                color: "#10B981",
                title: "واتساب",
                desc: "للمساعدة الفورية أيام العمل",
                value: "تواصل عبر واتساب",
                href: "https://wa.me/966500000000",
              },
              {
                icon: Clock,
                color: "#A78BFA",
                title: "أوقات الدعم",
                desc: "الأحد — الخميس",
                value: "9:00ص – 6:00م (بتوقيت الرياض)",
                href: null,
              },
            ].map(({ icon: Icon, color, title, desc, value, href }) => (
              <div key={title} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:border-[#004EEB]/20 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${color}15` }}>
                    <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm mb-0.5">{title}</div>
                    <div className="text-slate-400 text-xs mb-2">{desc}</div>
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer"
                        className="text-[#004EEB] text-sm font-semibold hover:underline transition-colors">
                        {value}
                      </a>
                    ) : (
                      <span className="text-slate-600 text-sm">{value}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
              {status === "sent" ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">تم إرسال رسالتك!</h3>
                  <p className="text-slate-500 text-sm max-w-sm">
                    شكراً للتواصل معنا. سيرد عليك فريقنا على بريدك الإلكتروني خلال 24 ساعة.
                  </p>
                  <button onClick={() => { setStatus("idle"); setForm({ name: "", email: "", subject: "", message: "" }); }}
                    className="mt-6 px-5 py-2.5 text-sm font-semibold text-[#004EEB] border border-[#004EEB]/20 rounded-xl hover:bg-[#004EEB]/5 transition-colors">
                    إرسال رسالة أخرى
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-1">أرسل لنا رسالة</h2>
                    <p className="text-slate-400 text-sm">سنرد على بريدك الإلكتروني في أقرب وقت.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">الاسم</label>
                      <input className={inputCls} placeholder="اسمك الكريم" value={form.name}
                        onChange={e => update("name", e.target.value)} required />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">البريد الإلكتروني</label>
                      <input className={inputCls} type="email" placeholder="example@store.com" value={form.email}
                        onChange={e => update("email", e.target.value)} required />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">الموضوع</label>
                    <select className={inputCls} value={form.subject} onChange={e => update("subject", e.target.value)}>
                      <option value="">اختر الموضوع</option>
                      <option>مشكلة تقنية</option>
                      <option>استفسار عن الأسعار</option>
                      <option>طلب ميزة جديدة</option>
                      <option>مشكلة في الربط مع سلة</option>
                      <option>مشكلة في الربط مع قيود</option>
                      <option>أخرى</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">الرسالة</label>
                    <textarea className={`${inputCls} resize-none`} rows={5}
                      placeholder="اكتب رسالتك هنا بالتفصيل..." value={form.message}
                      onChange={e => update("message", e.target.value)} required />
                  </div>

                  <button type="submit" disabled={status === "sending"}
                    data-testid="contact-submit"
                    className="w-full flex items-center justify-center gap-2 bg-[#004EEB] hover:bg-[#003BBB] disabled:opacity-70 text-white py-3.5 rounded-xl font-bold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-200">
                    {status === "sending"
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري الإرسال...</>
                      : <><Send className="w-4 h-4" /> إرسال الرسالة</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="border-t border-slate-200 py-8 text-center px-4">
        <div className="flex items-center justify-center gap-4 text-sm">
          <Link to="/" className="text-[#004EEB] hover:underline">الرئيسية</Link>
          <span className="text-slate-300">·</span>
          <Link to="/privacy" className="text-[#004EEB] hover:underline">الخصوصية</Link>
          <span className="text-slate-300">·</span>
          <Link to="/terms" className="text-[#004EEB] hover:underline">الشروط</Link>
        </div>
      </div>
    </div>
  );
}
